import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * The data instruments: ledger readouts, gauges and headline figures.
 *
 * These are the shapes that actually carry money on screen, so they are flat and quiet —
 * no rivets, no brass, minimal shadow. The material language lives in the chrome around
 * them (see ui/instrument.tsx); putting it behind a column of figures would cost legibility
 * and buy nothing.
 *
 * Semantic tone is not decoration here. Per the deck: brass is reward, yield, caps and
 * multiples; fluid is stables, liquidity and flow; enamel is protocol figures and rates;
 * fault is penalty and forfeiture. A number's colour is a claim about what kind of number
 * it is, and the two `-text` tokens each tone uses are the ones validated in
 * src/lib/color.test.ts.
 */

export type Tone = 'ink' | 'enamel' | 'brass' | 'fluid' | 'fault' | 'muted'

/** Eyebrow dot, one per tone. See the StatBlock note on why it is not always green. */
const TONE_DOT: Record<Tone, string> = {
  ink: 'bg-line-strong',
  muted: 'bg-line-strong',
  enamel: 'bg-accent',
  brass: 'bg-brass',
  fluid: 'bg-fluid',
  fault: 'bg-fault',
}

const TONE_TEXT: Record<Tone, string> = {
  ink: 'text-ink',
  muted: 'text-muted',
  enamel: 'text-accent',
  brass: 'text-brass-text',
  fluid: 'text-fluid-text',
  fault: 'text-fault-text',
}

// ─────────────────────────── Readout ───────────────────────────

/**
 * A grouped ledger. Rows divide themselves; this draws the boundary and clips the corners.
 *
 * This is the single most reused shape in the redesign — the landing page's bond calculator,
 * the transparency balance sheet and every "your deposit" summary are all this component.
 * Before, each of those was its own hand-rolled flex row, and they had drifted.
 */
export function Readout({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'surface-glass-2 divide-y divide-line overflow-hidden rounded-[10px] ring-1 ring-line',
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * One line of a ledger: a mono label on the left, a mono tabular figure on the right.
 *
 * `net` marks the row a total lands on. It gets the accent wash, a larger figure and an
 * accent label — the landing page's "NET VALUE" treatment. Use it once per readout; two
 * highlighted totals in one column means neither is the total.
 */
export function ReadoutRow({
  label,
  value,
  tone = 'ink',
  net = false,
  dense = false,
  note,
  className,
}: {
  label: ReactNode
  value: ReactNode
  tone?: Tone
  net?: boolean
  /**
   * Tighter rows, for the navigation rail.
   *
   * Not a stylistic option — a six-row readout at the default height pushed the rail's
   * content past the viewport and clipped the masthead off the top of the page. Ambient
   * reference figures in a 280px column do not need the same room as a settlement breakdown
   * someone is checking line by line.
   */
  dense?: boolean
  note?: ReactNode
  className?: string
}) {
  return (
    /*
     * ── The probe ──
     * Hovering a line slides a fluid rule in from the left edge and lifts the figure to full
     * strength. It reads as putting an instrument on that row, which is what hovering a ledger
     * line in this product actually is.
     *
     * Built from `scaleX` with a left origin rather than an animated width, so it composites and
     * cannot reflow the row it is annotating. Hover-driven, so the reader is present and the
     * background-tab freeze does not apply.
     */
    <div
      className={cn(
        'group relative flex items-baseline justify-between gap-4',
        dense ? 'px-3 py-1.5' : 'px-4 py-2.5',
        net && 'bg-accent-wash',
        className,
      )}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[2px] origin-left scale-y-0 bg-fluid transition-transform duration-200 ease-out group-hover:scale-y-100"
      />
      <span className={cn('label min-w-0 transition-colors duration-200', net ? 'text-accent' : 'group-hover:text-muted')}>
        {label}
      </span>
      <span className="shrink-0 text-right">
        <span
          className={cn(
            'tnum transition-transform duration-200 group-hover:-translate-x-0.5',
            net ? 'text-lg font-semibold' : dense ? 'text-xs' : 'text-sm',
            TONE_TEXT[tone],
          )}
        >
          {value}
        </span>
        {note && <span className="mt-0.5 block text-[10px] text-faint">{note}</span>}
      </span>
    </div>
  )
}

// ─────────────────────────── Gauge ───────────────────────────

/**
 * An inset track with a fluid fill, and optionally a brass ceiling marker — the deck's
 * "2.5× CEILING" device.
 *
 * Both `value` and `ceiling` are clamped to 0..1. A gauge is a claim about a proportion, and
 * a fill wider than its track is a lie about the data; over-committed and over-capped are
 * both real states, so they read as *full* rather than as overflowing.
 */
export function Gauge({
  value,
  ceiling,
  ceilingLabel,
  tone = 'fluid',
  className,
}: {
  /** 0..1 */
  value: number
  /** 0..1 — draws a dashed brass marker. */
  ceiling?: number
  ceilingLabel?: string
  tone?: 'fluid' | 'enamel'
  className?: string
}) {
  const clamp = (n: number) => Math.min(1, Math.max(0, Number.isFinite(n) ? n : 0))
  const pct = clamp(value) * 100
  const cap = ceiling === undefined ? undefined : clamp(ceiling) * 100

  return (
/*
     * A hover-reveal plate showing the exact percentage was tried here and REMOVED.
     *
     * Two things were wrong with it. It collided with the caption sitting to the right of the
     * track on the transparency page — and more importantly it was redundant: at every call site
     * in this app the figure is already printed beside the gauge, so the plate duplicated a number
     * the reader could already see. An interaction that reveals something already visible is
     * decoration, and this file's own rule is that a gauge reports a proportion and nothing else.
     */
    <div className={cn('relative', ceilingLabel && 'pt-4', className)}>
      {cap !== undefined && ceilingLabel && (
        <span
          className="label absolute top-0 -translate-x-1/2 whitespace-nowrap text-brass-text"
          style={{ left: `${cap}%` }}
        >
          {ceilingLabel}
        </span>
      )}
      <div className="relative">
        <div
          className="relative h-1.5 overflow-hidden rounded-full"
          style={{ background: 'var(--track-bg)', boxShadow: 'var(--track-shadow)' }}
          role="meter"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {/*
           * A CSS TRANSITION, not a keyframed animation, and no `initial` frame.
           *
           * The first attempt animated width from a 0% initial frame with Framer, and the
           * fill stayed pinned at 0% forever — measured at 0% fourteen seconds after load.
           * That is the failure theme.css already documents for the page entrance: Chrome
           * freezes animation timelines in a background tab, reporting playState "running"
           * with currentTime stuck at 0, so whatever the 0% frame says becomes permanent.
           *
           * Here that meant a gauge reading EMPTY regardless of its actual value — the worst
           * possible outcome for a component whose only job is to report a proportion, and
           * silent, because nothing errors.
           *
           * A transition cannot fail that way. `width` is set to the real value, so the
           * element's natural state is already correct; if the transition never runs, the
           * fill simply appears at its value with no animation. It also gives the better
           * behaviour: no theatre on mount, movement only when the number actually changes,
           * which is the rule motion.ts states. The global prefers-reduced-motion block
           * zeroes the duration, so there is nothing to branch on here.
           */}
          <div
            className="h-full rounded-full transition-[width] duration-500 ease-out"
            style={{
              width: `${pct}%`,
              background: tone === 'fluid' ? 'var(--m-gauge-fluid)' : 'var(--m-gauge-enamel)',
            }}
          />
        </div>
        {/*
         * The ceiling tick. It deliberately OVERHANGS the track top and bottom rather than
         * sitting inside it: a marker clipped to a 6px track is invisible at a glance, which
         * defeats the entire point of drawing a published limit.
         */}
        {cap !== undefined && (
          <span
            aria-hidden
            className="absolute -top-1 -bottom-1 w-0.5 rounded-full bg-brass"
            style={{ left: `${cap}%`, marginLeft: '-1px' }}
          />
        )}
      </div>
    </div>
  )
}

// ─────────────────────────── StatBlock ───────────────────────────

/**
 * A headline figure.
 *
 * ── Structure, and why it was inverted ──
 * Label ABOVE the value as a quiet tracked eyebrow, value large, prose caption below. This
 * matches the supplied Pots Money reference, and it reverses an earlier decision here to put the
 * label underneath.
 *
 * The old rationale was that label-below lets a row of figures scan as magnitudes first and names
 * second. That holds, but it assumed the label competed with the value — and once the label is a
 * 10px tracked eyebrow rather than a display-weight word, it stops competing and simply says what
 * you are about to read. Eyebrow-above is also the convention almost every reader already knows.
 *
 * ── The dot ──
 * The reference marks every eyebrow with a single green dot. Here it takes the value's own
 * semantic tone instead, so a brass dot means the figure is a reward, fluid means a stablecoin
 * amount, and so on — the same rule the rest of the interface follows. A plain count gets a quiet
 * neutral dot rather than borrowing a meaning it has no claim to.
 *
 * The eyebrow stays MONO, not the display face the reference uses. Every other label in this app
 * is mono, tracked and uppercase, and that is the instrument voice the whole design rests on;
 * making these three sans would leave them the only labels in the product that disagree.
 */
export function StatBlock({
  label,
  value,
  unit,
  note,
  tone = 'ink',
  size = 'md',
  className,
}: {
  label: ReactNode
  value: ReactNode
  unit?: string
  note?: ReactNode
  tone?: Tone
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const valueSize = { sm: 'text-2xl', md: 'text-4xl', lg: 'text-5xl sm:text-6xl' }[size]

  return (
    <div className={className}>
      <p className="label flex items-center gap-2">
        <span
          aria-hidden
          className={cn('size-1.5 shrink-0 rounded-full', TONE_DOT[tone])}
        />
        {label}
      </p>
      <p
        className={cn(
          'display mt-2 tabular-nums',
          // Headline sizes only — see the optical-sizing note on `.display` in theme.css.
          size !== 'sm' && 'display-tight',
          valueSize,
          TONE_TEXT[tone],
        )}
      >
        {value}
        {unit && (
          <span className="ml-1.5 font-mono text-xs font-normal tracking-normal text-faint">
            {unit}
          </span>
        )}
      </p>
      {note && <p className="mt-2.5 max-w-prose text-sm leading-relaxed text-muted">{note}</p>}
    </div>
  )
}
